import { useTranslations } from '@/i18n/translations';
import { useLocation } from '@tanstack/react-router';
import { Button, EmptyState, I, Stack } from '@leanin/ui';
import { RouterLinkAs } from '@/lib/router-link';
import { withReturnPath } from '@/lib/return-path';
import { useSession } from '@/lib/use-session';
import { useServerBodyHandoff } from '@/lib/server-body-handoff';

export type NotFoundPageProps = {
  /** Entity-specific copy for signed-out visitors. A members-only entity
   *  answers an anonymous viewer with the same 404 as a missing one, so this
   *  copy must explain that signing in may help without claiming the entity
   *  exists. Omitted → the generic signed-out copy. */
  readonly signedOut?: { readonly title: string; readonly body: string };
};

// Signed-in viewers get the in-app recovery links. Signed-out visitors can't
// use those (they bounce to sign-in and lose the URL), and the 404 they hit
// may be a members-only page, so they get a sign-in that returns them here.
export function NotFoundPage({ signedOut }: NotFoundPageProps) {
  // A 404 or 410 is a real answer: the page is gone, so the server body's
  // copy of it must not stay up.
  useServerBodyHandoff(true);
  const t = useTranslations('Shell');
  const session = useSession();
  const here = useLocation({ select: (location) => location.href });

  // A failed session probe degrades to signed-out, matching the `_public`
  // layout's own fallback.
  const signedOutViewer =
    session.data === null || (session.data === undefined && session.isError);

  if (signedOutViewer) {
    return (
      <Stack align="center" justify="center" py="page" style={{ minHeight: '60vh' }}>
        <EmptyState
          density="page"
          icon={I.lock}
          title={signedOut?.title ?? t('notFound.signedOut.title')}
          body={signedOut?.body ?? t('notFound.signedOut.body')}
          primary={
            <Button
              variant="primary"
              href={withReturnPath('/sign-in', here)}
              linkAs={RouterLinkAs}
            >
              {t('notFound.signedOut.signIn')}
            </Button>
          }
          secondary={
            <Button variant="secondary" href="/get-started" linkAs={RouterLinkAs}>
              {t('notFound.signedOut.join')}
            </Button>
          }
        />
      </Stack>
    );
  }

  return (
    <Stack align="center" justify="center" py="page" style={{ minHeight: '60vh' }}>
      <EmptyState
        density="page"
        icon={I.search}
        eyebrow="404"
        title={t('notFound.title')}
        body={t('notFound.body')}
        {...(session.data === undefined
          ? {}
          : {
              primary: (
                <Button variant="primary" href="/feed" linkAs={RouterLinkAs}>
                  {t('notFound.goHome')}
                </Button>
              ),
              secondary: (
                <Button variant="secondary" href="/people" linkAs={RouterLinkAs}>
                  {t('notFound.browsePeople')}
                </Button>
              ),
            })}
      />
    </Stack>
  );
}
